import { Maybe } from '@codegrade/maybe';
import { expr, itertools, map } from '@codegrade/utils';
/**
 * Aggregator for generated message chunks.
 *
 * This class will make sure output chunks are always concatenated in the
 * correct order, and keeps track of if every part of a message has been
 * received.
 */
export class MessageAggregator {
    message;
    nextExpectedIndex;
    enqueuedChunks;
    content;
    generationId;
    /**
     * Construct a new Message aggregator.
     *
     * Note: You most likely want to use `create` instead.
     *
     * @param message The message being generated, excluding its data.
     * This is used to maintain the metadata of the message throughout its lifecycle.
     * @param nextExpectedIndex The next index we expect to receive.
     * This ensures that message chunks are processed in the correct order.
     * @param enqueuedChunks Queue for messages with a higher index than the one we need next.
     * This allows for handling out-of-order message chunks by storing them until they can be processed.
     * @param content The current content of the message being generated.
     * This accumulates the text from each processed chunk.
     * @param generationId The unique identifier for the message generation process.
     * This is used to ensure that chunks belong to the correct message generation.
     */
    constructor(
    /** The message being generated. */
    message, 
    /** The next index we expect to receive. */
    nextExpectedIndex, 
    /** Queue for messages with a higher index than the one we need next. */
    enqueuedChunks, content, generationId) {
        this.message = message;
        this.nextExpectedIndex = nextExpectedIndex;
        this.enqueuedChunks = enqueuedChunks;
        this.content = content;
        this.generationId = generationId;
        Object.freeze(this);
    }
    /** @returns The message identifier, sequence number, unique within one conversation. */
    get messageId() {
        return this.message.messageId;
    }
    /**
     * Create a new message aggregator from a "generation-started" update message.
     *
     * @param assistantId The assistant in which this message is posted.
     * @param conversationId The conversation in which this message is posted.
     * @param update The "generation-started" update message.
     * @returns A new `MessageAggregator` instance.
     */
    static create(assistantId, conversationId, update) {
        return new MessageAggregator(Object.freeze({
            assistantId,
            conversationId,
            messageId: update.messageId.toString(),
            createdAt: update.createdAt.toDate(),
        }), 0, new map.ReadonlyMaybeMap(), '', update.generationId);
    }
    /**
     * Process an output chunk.
     *
     * @param update The "output-chunk" websocket update.
     * @returns `Nothing` if the received update could not be applied yet
     * because we haven't received an earlier chunk yet. Otherwise the updated
     * message wrapped in a `Just`.
     */
    processChunk(update) {
        if (update.generationId !== this.generationId) {
            return this;
        }
        let content = this.content;
        if (update.index === this.nextExpectedIndex) {
            content += update.text;
            let newIndex = this.nextExpectedIndex + 1;
            for (let chunk = this.enqueuedChunks.get(newIndex); chunk.isJust(); chunk = this.enqueuedChunks.get(++newIndex)) {
                content += chunk.value.text;
            }
            return new MessageAggregator(this.message, newIndex, this.enqueuedChunks, content, this.generationId);
        }
        else {
            return new MessageAggregator(this.message, this.nextExpectedIndex, new map.ReadonlyMaybeMap(itertools.chain([
                this.enqueuedChunks.entries(),
                [[update.index, update]],
            ])), this.content, this.generationId);
        }
    }
    /**
     * Finalize the message being generated.
     *
     * @param update The "generation-ended" websocket update.
     * @returns `Nothing` if not all chunks of the message have been received.
     * Otherwise the final message wrapped in a `Just`.
     */
    finalize(update) {
        if (update.totalChunks !== this.nextExpectedIndex) {
            return Maybe.empty();
        }
        return Maybe.of({
            ...this.message,
            data: {
                assistant: {
                    content: { text: this.content },
                    stopReason: expr.match(update.stopReason, 'reason', {
                        end_turn: () => ({
                            endTurn: {},
                        }),
                        max_tokens: ({ maxTokens }) => ({
                            maxTokens: { maxTokens },
                        }),
                    }),
                },
            },
        });
    }
}
